/**
 * Parser del Excel que el admin sube para asignar fechas a las clases
 * cuando crea un curso desde plantilla.
 *
 * Formato esperado (basado en "Calendario y Usuarios Diplomado 010.xlsx"):
 *
 *    Hoja:    "Fechas Clases"  (si no existe, usa la primera)
 *    Columnas:
 *      MODULOS  →  "CLASE 1", "CLASE 2", ..., "ENTREGA 1", "ENTREGA FINAL"
 *      TEMAS    →  texto descriptivo (opcional)
 *      FECHA    →  fecha (datetime o string ISO)
 *
 * El parser es tolerante:
 *   - Acepta mayúsculas/minúsculas, tildes y dobles espacios
 *   - Acepta "ENTREGA FINAL" → delivery_number = 99
 *   - Acepta fechas como Date object, serial Excel, o string "YYYY-MM-DD"
 *   - Reporta errores específicos en `errors[]` sin tirar excepciones
 *
 * La librería xlsx (SheetJS) se importa dinámicamente para no engordar
 * el bundle inicial — solo se carga cuando el admin abre el modal.
 */
import type { CalendarRow, ParsedCalendar } from '../types/templates';

/** Lee un archivo Excel y devuelve clases + entregas + errores. */
export async function parseCalendarExcel(file: File): Promise<ParsedCalendar> {
  const errors: string[] = [];
  const classes:    CalendarRow[] = [];
  const deliveries: CalendarRow[] = [];

  try {
    // Lazy import — solo carga xlsx cuando realmente se usa
    const XLSX = await import('xlsx');

    const buf = await file.arrayBuffer();
    const wb  = XLSX.read(buf, { type: 'array', cellDates: true });

    // Buscar la hoja correcta: preferimos "Fechas Clases" (con o sin tilde),
    // si no existe usamos la primera.
    const targetName = wb.SheetNames.find(n =>
      n.toLowerCase().replace(/\s+/g, ' ').includes('fechas')
    ) ?? wb.SheetNames[0];

    if (!targetName) {
      errors.push('El archivo no tiene hojas.');
      return { classes, deliveries, errors };
    }

    const ws = wb.Sheets[targetName];
    // Convertir a matriz de filas (sheet_to_json con header:1 da array<array>)
    const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: null });

    // Buscar la columna de "modulo", "tema" y "fecha" (header tolerante)
    let colModulo = 0;
    let colTema   = 1;
    let colFecha  = 2;
    let headerFound = false;

    for (let r = 0; r < Math.min(rows.length, 10); r++) {
      const row = rows[r] as unknown[];
      const idx = (kw: string) => row.findIndex(
        (c) => typeof c === 'string' && normalize(c).includes(kw)
      );
      const iMod   = idx('modulo');
      const iFecha = idx('fecha');
      if (iMod >= 0 && iFecha >= 0) {
        colModulo = iMod;
        const iTema = idx('tema');
        if (iTema >= 0) colTema = iTema;
        colFecha = iFecha;
        headerFound = true;
        // saltamos la fila de header
        for (let i = r + 1; i < rows.length; i++) parseRow(rows[i] as unknown[], i + 1);
        break;
      }
    }

    // Si no encontró header, parseamos asumiendo orden estándar (col 0,1,2)
    // y saltamos eventual primera fila si parece header
    if (!headerFound) {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as unknown[];
        if (i === 0 && row.some(c => typeof c === 'string' && /modulo|fecha/i.test(c as string))) continue;
        parseRow(row, i + 1);
      }
    }

    function parseRow(row: unknown[], rowNum1Based: number) {
      const labelRaw = row?.[colModulo];
      const dateRaw  = row?.[colFecha];
      const temaRaw  = row?.[colTema];
      if (labelRaw == null || dateRaw == null) return;
      const labelStr = String(labelRaw).trim();
      if (!labelStr) return;

      // Detectar CLASE N
      const mClase = /^CLASE\s+(\d+)$/i.exec(labelStr);
      if (mClase) {
        const n = parseInt(mClase[1], 10);
        const iso = toIsoDate(dateRaw);
        if (!iso) {
          errors.push(`Fila ${rowNum1Based}: "${labelStr}" tiene una fecha inválida.`);
          return;
        }
        classes.push({
          raw_label:       labelStr,
          class_number:    n,
          delivery_number: null,
          date:            iso,
          display_label:   String(temaRaw ?? '').trim() || `Clase ${n}`,
        });
        return;
      }

      // Detectar ENTREGA N (o ENTREGA FINAL)
      const mEntrega = /^ENTREGA\s+(\d+|FINAL)$/i.exec(labelStr);
      if (mEntrega) {
        const token = mEntrega[1].toUpperCase();
        const num = token === 'FINAL' ? 99 : parseInt(token, 10);
        const iso = toIsoDate(dateRaw);
        if (!iso) {
          errors.push(`Fila ${rowNum1Based}: "${labelStr}" tiene una fecha inválida.`);
          return;
        }
        deliveries.push({
          raw_label:       labelStr,
          class_number:    null,
          delivery_number: num,
          date:            iso,
          display_label:   String(temaRaw ?? '').trim() || labelStr,
        });
        return;
      }

      // Si tiene fecha pero el label no encaja, lo reportamos
      errors.push(`Fila ${rowNum1Based}: no reconozco el formato del módulo "${labelStr}" (esperado: "CLASE N" o "ENTREGA N").`);
    }

    // Ordenar por fecha (por las dudas)
    classes.sort((a, b)    => a.class_number!    - b.class_number!);
    deliveries.sort((a, b) => a.delivery_number! - b.delivery_number!);

    if (classes.length === 0 && deliveries.length === 0) {
      errors.push('No se detectaron clases ni entregas en el archivo.');
    }
  } catch (e: any) {
    errors.push('Error leyendo el archivo: ' + (e?.message ?? String(e)));
  }

  return { classes, deliveries, errors };
}

// ─── Helpers ───────────────────────────────────────────────────────────

/** Normaliza string para comparar headers (lowercase, sin tildes, sin espacios extra). */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')   // quitar tildes
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Convierte distintos formatos de fecha del Excel a "YYYY-MM-DD".
 * Soporta: Date object, ISO string, "DD/MM/YYYY", número serial de Excel.
 * Devuelve null si no puede parsear.
 */
function toIsoDate(v: unknown): string | null {
  if (v == null) return null;

  // Date object (cuando xlsx lee con cellDates:true)
  if (v instanceof Date && !isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }

  // Número (serial date de Excel — días desde 1899-12-30)
  if (typeof v === 'number' && isFinite(v)) {
    const ms = (v - 25569) * 86400 * 1000;
    const d  = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }

  // String — probamos varios formatos
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;

    // ISO o "YYYY-MM-DD HH:MM:SS" → primer cacho YYYY-MM-DD
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    // DD/MM/YYYY o DD-MM-YYYY
    const dmy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/.exec(s);
    if (dmy) {
      const dd = dmy[1].padStart(2, '0');
      const mm = dmy[2].padStart(2, '0');
      let yy = dmy[3];
      if (yy.length === 2) yy = (parseInt(yy, 10) >= 70 ? '19' : '20') + yy;
      return `${yy}-${mm}-${dd}`;
    }

    // Último intento: Date.parse
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return null;
}
